import type { FormErrors } from 'redux-form';

import {
  ANNOUNCEMENT_FIELD,
  FormHeaders,
  FormVariables,
  HEADERS_FIELD,
  IntegrationModalFormValues,
  Props as IntegrationModalProps,
  URL_FIELD,
  VARIABLES_FIELD,
} from 'components/powerFlowShared/components/modals/integration';
import {
  KEY_FIELD,
  VALUE_FIELD,
} from 'components/powerFlowShared/components/modals/integration/headerRow';
import {
  ID_FIELD,
  PATH_FIELD,
} from 'components/powerFlowShared/components/modals/integration/variableRow';
import {
  getVariableNameErrorMessage,
  isValidVariableName,
} from 'components/powerFlowShared/helpers/flowVariableHelpers';
import { FlowType } from 'fable/flowEditor';

type VariableErrorTypeT = string | FormErrors<FormVariables>;
type HeaderErrorTypeT = string | FormErrors<FormHeaders>;
export type ErrorTypeT =
  | VariableErrorTypeT
  | VariableErrorTypeT[]
  | HeaderErrorTypeT
  | HeaderErrorTypeT[];

interface Errors {
  [ANNOUNCEMENT_FIELD]?: string;
  [HEADERS_FIELD]?: HeadersError[];
  [URL_FIELD]?: string;
  [VARIABLES_FIELD]?: VariablesError[];
}

type HeadersError = {
  [Key in typeof KEY_FIELD | typeof VALUE_FIELD]?: string;
};

type VariablesError = {
  [Key in typeof PATH_FIELD | typeof ID_FIELD]?: string;
};

export const validate = (
  { announcement, headers, variables, url }: IntegrationModalFormValues,
  { flowType, scopeVars: { existing, grants } }: IntegrationModalProps,
): FormErrors => {
  const errors: Errors = {};

  // Create a blacklist with variables created outside this modal
  const blackList = existing.filter(
    (name: string) => !grants.includes(name) && name !== '',
  );

  if (!url?.trim()) {
    errors[URL_FIELD] = 'Please fill url';
  }

  if (variables) {
    const variablesErrors: Errors[typeof VARIABLES_FIELD] = [];

    variables.forEach(({ id, locked, path }: FormVariables, index: number) => {
      const variablesError: VariablesError = {};

      if (!path) {
        variablesError[PATH_FIELD] = 'Adding a JSON path is required';
      }

      if (!id) {
        variablesError[ID_FIELD] = 'Adding a variable is required';
      } else if (!isValidVariableName(id)) {
        const nameErrorMessage = getVariableNameErrorMessage(id);

        if (nameErrorMessage) {
          variablesError[ID_FIELD] = nameErrorMessage;
        }
      }

      // Don't run the rest of the validation if the variable is locked,
      // because we can assume it wasn't changed
      if (!locked) {
        // Check if the variable is already used somewhere outside this modal
        if (blackList.includes(id)) {
          variablesError[ID_FIELD] = 'This variable name was used elsewhere';
        } else {
          // Check if the variable is already used inside this modal
          variables.forEach(
            (nestedVariable: FormVariables, nestedIndex: number) => {
              if (nestedIndex !== index && nestedVariable.id === id) {
                variablesError[ID_FIELD] =
                  'This variable name is already in use';
              }
            },
          );
        }
      }

      if (Object.keys(variablesError).length) {
        variablesErrors[index] = variablesError;
      }
    });

    if (variablesErrors.length) {
      errors[VARIABLES_FIELD] = variablesErrors;
    }
  }

  if (headers) {
    const headersErrors: Errors[typeof HEADERS_FIELD] = [];

    headers.forEach(({ key, value }: FormHeaders, index: number) => {
      const headersError: HeadersError = {};

      if (!key) {
        headersError[KEY_FIELD] = 'Adding a key is required';
      }

      if (!value) {
        headersError[VALUE_FIELD] = 'Adding a value is required';
      }

      if (Object.keys(headersError).length) {
        headersErrors[index] = headersError;
      }
    });

    if (headersErrors.length) {
      errors[HEADERS_FIELD] = headersErrors;
    }
  }

  if (flowType === FlowType.FLOW_TYPE_CALL && !announcement) {
    errors[ANNOUNCEMENT_FIELD] = 'Please choose an announcement';
  }

  return errors;
};
